import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ExceptionRateRule,
    ExceptionRatesFrom,
    ExceptionSettlementMode,
    exceptionSaveFormatOf,
    isExceptionRatesVisibleInMode,
    useCoachCommissionTables,
} from '@/features/coachCommissionTable';
import { useSettlementCategories } from '@/features/settlementClassification';
import { useAccessMode } from '@/context/AccessModeContext';
import ExceptionRatesModal from './ExceptionRatesModal';
import CopyExceptionRatesDialog from './CopyExceptionRatesDialog';

type ExceptionRatesEntry = { rules: ExceptionRateRule[]; from: ExceptionRatesFrom | null };

/**
 * 요율 탭(기본·상세·세션별 정산) 공통 — 예외 수업료 규칙 상태와 창 두 개(편집·다른 강사에게 복사).
 *
 * - 규칙은 요율 조회(GET coachCommissionTables)에 실려 온다. 같은 조회 키라 상세 정산 탭과 요청을 나눠 쓴다.
 * - 입구를 보일지는 isExceptionRatesVisibleInMode — 새 서버(exceptionRatesAllModes)면 세 방식 모두,
 *   이전 서버면 지금처럼 상세 정산(exceptionRatesEffective)에서만.
 * - 저장은 전용 PUT 으로 강사 1명씩 한다. 저장 뒤 요율 목록은 다시 읽지 않는다(hooks.ts 의 refetchType: 'none'
 *   — 편집 중인 구간표가 서버 값으로 되돌아가지 않게). 화면 표시는 응답값으로 여기서 바로 고친다.
 */
export const useExceptionRatesEditor = ({
    centerId,
    year,
    month,
    settlementMode,
    enabled = true,
    baseRateMissing,
}: {
    centerId?: string;
    year: string;
    month: string;
    settlementMode: ExceptionSettlementMode;
    /** false 면 조회하지 않고 아무것도 보이지 않는다(이 탭이 열려 있지 않을 때). */
    enabled?: boolean;
    /** 기본 정산: 강사의 이 달 기본 비율이 서버에 없는가(창에 안내를 띄운다). */
    baseRateMissing?: (coachId: string) => boolean;
}) => {
    const { data: commissionData } = useCoachCommissionTables({
        centerId: centerId || '',
        year,
        month,
        enabled: enabled && !!centerId,
    });
    const { isReadOnly } = useAccessMode();
    const visible = enabled && !!centerId && isExceptionRatesVisibleInMode(commissionData, settlementMode);
    // 이전 서버(exceptionRatesAllModes 없음)에는 예전 형식으로 저장한다 — 새 키·표식을 400 으로 거부한다.
    const saveFormat = exceptionSaveFormatOf(commissionData);

    const [exceptionMap, setExceptionMap] = useState<Record<string, ExceptionRatesEntry>>({});
    const [modalCoachId, setModalCoachId] = useState<string | null>(null);
    const [copySourceCoachId, setCopySourceCoachId] = useState<string | null>(null);

    const { data: categoriesState } = useSettlementCategories({
        centerId: centerId || '',
        enabled: visible,
    });
    const categories = useMemo(() => categoriesState?.categories ?? [], [categoriesState?.categories]);
    const categoryName = useCallback(
        (key: string) => categories.find((c) => c.key === key)?.name ?? '알 수 없는 분류',
        [categories],
    );

    const coaches = useMemo(
        () =>
            (commissionData?.coachCommissionTables ?? []).map((coach) => ({
                coachId: coach.coachId,
                coachName: coach.coachName,
            })),
        [commissionData],
    );

    useEffect(() => {
        if (!commissionData?.coachCommissionTables) return;
        const next: Record<string, ExceptionRatesEntry> = {};
        commissionData.coachCommissionTables.forEach((coach) => {
            next[coach.coachId] = {
                rules: Array.isArray(coach.exceptionRates) ? coach.exceptionRates : [],
                from: coach.exceptionRatesFrom ?? null,
            };
        });
        setExceptionMap(next);
    }, [commissionData]);

    const handleSaved = useCallback((coachId: string, rules: ExceptionRateRule[], from: ExceptionRatesFrom | null) => {
        setExceptionMap((prev) => ({ ...prev, [coachId]: { rules, from } }));
    }, []);

    const rulesOf = useCallback((coachId: string) => exceptionMap[coachId]?.rules ?? [], [exceptionMap]);
    /**
     * 이 강사의 이 달 IN 구간표가 0원이 될 수 있는 이유(서버 inRateZeroable, 저장된 표 기준). 상세 정산에서만 —
     * 「항상 IN 요율의 배수」는 상세 정산에서만 계산된다. 적용하기 전 구간표 편집은 반영하지 못한다.
     */
    const zeroableOf = useCallback(
        (coachId: string) =>
            settlementMode === 'tier'
                ? commissionData?.coachCommissionTables?.find((coach) => coach.coachId === coachId)?.inRateZeroable ?? null
                : null,
        [commissionData, settlementMode],
    );
    /**
     * 복사 창용 — 방식과 상관없이 본다. 기본·세션별 정산에서도 숨겨진 「항상 IN 요율의 배수」까지 통째로 복사되고,
     * 나중에 상세 정산으로 바꾸면 그대로 적용되기 때문이다(웹 2단계 검증 P2).
     */
    const copyZeroableOf = useCallback(
        (coachId: string) =>
            commissionData?.coachCommissionTables?.find((coach) => coach.coachId === coachId)?.inRateZeroable ?? null,
        [commissionData],
    );
    /** 이 강사가 요율 조회에 있는가(없으면 저장할 수 없다 — 센터에 연결된 강사만 온다). */
    const hasCoach = useCallback((coachId: string) => coaches.some((c) => c.coachId === coachId), [coaches]);
    const nameOf = (coachId: string) => coaches.find((c) => c.coachId === coachId)?.coachName ?? '';

    const dialogs =
        visible && centerId ? (
            <>
                {modalCoachId && (
                    <ExceptionRatesModal
                        centerId={centerId}
                        year={year}
                        month={month}
                        coachId={modalCoachId}
                        coachName={nameOf(modalCoachId)}
                        rules={rulesOf(modalCoachId)}
                        rulesFrom={exceptionMap[modalCoachId]?.from ?? null}
                        categories={categories}
                        settlementMode={settlementMode}
                        saveFormat={saveFormat}
                        baseRateMissing={!!baseRateMissing?.(modalCoachId)}
                        inRateZeroable={zeroableOf(modalCoachId)}
                        readOnly={isReadOnly}
                        onClose={() => setModalCoachId(null)}
                        onSaved={handleSaved}
                        onCopy={() => {
                            setCopySourceCoachId(modalCoachId);
                            setModalCoachId(null);
                        }}
                    />
                )}
                {copySourceCoachId && (
                    <CopyExceptionRatesDialog
                        centerId={centerId}
                        year={year}
                        month={month}
                        sourceCoachName={nameOf(copySourceCoachId)}
                        rules={rulesOf(copySourceCoachId)}
                        coaches={coaches.filter((c) => c.coachId !== copySourceCoachId)}
                        categoryName={categoryName}
                        settlementMode={settlementMode}
                        saveFormat={saveFormat}
                        zeroableOf={copyZeroableOf}
                        onClose={() => setCopySourceCoachId(null)}
                        onApplied={handleSaved}
                    />
                )}
            </>
        ) : null;

    return {
        visible,
        readOnly: isReadOnly,
        categoryName,
        rulesOf,
        hasCoach,
        open: setModalCoachId,
        dialogs,
    };
};

export type ExceptionRatesEditor = ReturnType<typeof useExceptionRatesEditor>;
